/**
 * Create/edit drawer for a single recurring operating-hours window.
 */

import { useEffect } from 'react';
import { Field, SimpleGrid, VStack } from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  DrawerComponent,
  InputField,
  SelectInput,
} from '@themes/aegix';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  operatingHoursFormSchema,
  type OperatingHoursFormValues,
} from '../../../validations/whakahaereConfig.validation';
import {
  WEEKDAY_LABELS,
  type OperatingHours,
  type Weekday,
} from '../../../services';
import {
  useCreateOperatingHours,
  useUpdateOperatingHours,
} from '../../../hooks/queries';
import { toaster } from '../../ui/toaster';

interface OperatingHoursDrawerProps {
  calendarId: number;
  isOpen: boolean;
  row: OperatingHours | null;
  onClose: () => void;
  onDelete: (id: number) => void;
}

const WEEKDAY_OPTIONS = (Object.keys(WEEKDAY_LABELS) as unknown as Weekday[]).map(
  (wd) => ({ value: String(wd), label: WEEKDAY_LABELS[wd] }),
);

function defaults(row: OperatingHours | null): OperatingHoursFormValues {
  return {
    weekday: row?.weekday ?? 0,
    open_time: row ? row.open_time.slice(0, 5) : '09:00',
    close_time: row ? row.close_time.slice(0, 5) : '15:00',
  };
}

export function OperatingHoursDrawer({
  calendarId,
  isOpen,
  row,
  onClose,
  onDelete,
}: OperatingHoursDrawerProps) {
  const createHours = useCreateOperatingHours();
  const updateHours = useUpdateOperatingHours(calendarId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<OperatingHoursFormValues>({
    defaultValues: defaults(row),
    resolver: zodResolver(operatingHoursFormSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    if (isOpen) reset(defaults(row));
  }, [isOpen, row, reset]);

  const onSubmit = async (values: OperatingHoursFormValues) => {
    try {
      if (row) {
        await updateHours.mutateAsync({
          id: row.id,
          data: {
            weekday: values.weekday as Weekday,
            open_time: values.open_time,
            close_time: values.close_time,
          },
        });
        toaster.create({ title: 'Hours updated', type: 'success' });
      } else {
        await createHours.mutateAsync({
          calendar: calendarId,
          weekday: values.weekday as Weekday,
          open_time: values.open_time,
          close_time: values.close_time,
        });
        toaster.create({ title: 'Hours added', type: 'success' });
      }
      onClose();
    } catch (err) {
      toaster.create({
        title: 'Could not save the hours',
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    }
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={row ? 'Edit hours' : 'Add hours'}
      size="sm"
      closeOnOutside={false}
      footer={
        <SimpleGrid columns={row ? 3 : 2} gap={3} width="100%">
          {row && (
            <ButtonComponent
              visual="alert"
              type="button"
              onClick={() => onDelete(row.id)}
              data-e2e="delete-hours-button"
            >
              Delete
            </ButtonComponent>
          )}
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={onClose}
            data-e2e="cancel-hours-button"
          >
            Cancel
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="submit"
            form="operating-hours-form"
            disabled={isSubmitting}
            data-e2e="save-hours-button"
          >
            Save
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <form id="operating-hours-form" onSubmit={handleSubmit(onSubmit)}>
        <VStack gap={4} align="stretch">
          <Field.Root invalid={!!errors.weekday}>
            <Field.Label htmlFor="hours-weekday">Day of week</Field.Label>
            <Controller
              name="weekday"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="hours-weekday"
                  value={String(field.value)}
                  option={WEEKDAY_OPTIONS}
                  onChange={(val: string) => field.onChange(Number(val))}
                  searchable={false}
                  e2eTitle="hours-weekday-select"
                />
              )}
            />
            {errors.weekday && (
              <Field.ErrorText>{errors.weekday.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.open_time}>
            <Field.Label htmlFor="hours-open">Opens</Field.Label>
            <Controller
              name="open_time"
              control={control}
              render={({ field }) => (
                <InputField
                  id="hours-open"
                  type="time"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  data-e2e="hours-open-input"
                />
              )}
            />
            {errors.open_time && (
              <Field.ErrorText>{errors.open_time.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.close_time}>
            <Field.Label htmlFor="hours-close">Closes</Field.Label>
            <Controller
              name="close_time"
              control={control}
              render={({ field }) => (
                <InputField
                  id="hours-close"
                  type="time"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  data-e2e="hours-close-input"
                />
              )}
            />
            {errors.close_time && (
              <Field.ErrorText>{errors.close_time.message}</Field.ErrorText>
            )}
          </Field.Root>
        </VStack>
      </form>
    </DrawerComponent>
  );
}
